import { useState, FormEvent, useMemo, useEffect } from "react";
import Form from "react-bootstrap/Form";
import { ButtonWithSpinner } from "../../../common/button-with-spinner/button-with-spinner";
import { resources } from "@constants/hardcoded-strings";
import { Company, RelyingParty, Assertion, RiskLevel } from "@idw-types/data/reference-data/generic";
import { RpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { OrganizationSelectFormGroup } from "../../../common/select-form-groups";
import { SelectOption } from "@idw-types/common";
import { SearchFormFilterRow } from "../../../common/search-form-filter-row/search-form-filter-row";
import { TimeRangeSearchFormFilterRow } from "../../../common/time-range-search-form-filter-row/time-range-search-form-filter-row";
import { TimeRangeTypes } from "@enums/time-range-types";
import { getTimeRange, getTimeRangeLabel } from "@utils/date-util";
import { sortStrings } from "@utils/string-util";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ButtonTypes } from "@enums/button-types";
import { MultiSelectFormGroup } from "../../../common/multi-select-form-group/multi-select-form-group";
import { MONTH_YEAR_INPUT_DATE_FORMAT } from "@constants/date-formats";
import { FraudReportSearchFiltersState } from "@idw-types/state/fraud-report-state";
import {
    mapAssertionsToSelectOptions,
    mapRiskLevelsToSelectOptions,
    mapSelectOptionsToValues,
    mapEntityWithNameAndIdToSelectOption,
    mapEntitiesWithNameAndIdToSelectOptions,
    mapRpServicesToSelectOptions
} from "@utils/select-option-mapping-util";
import Button from "react-bootstrap/Button";
import { initialFraudReportState } from "@redux/reducers/fraud-report-reducer";
import styles from "./fraud-report-search-form.less";
import { SearchFormHeaderContainer } from "../../../common/search-form-header-container/search-form-header-container";

type FraudReportSearchFormProps = {
    requestInProgress: boolean;
    companies: Array<Company>;
    relyingParties: Array<RelyingParty>;
    rpServices: Array<RpServiceConfiguration>;
    assertions: Array<Assertion>;
    riskLevels: Array<RiskLevel>;
    searchFilters: FraudReportSearchFiltersState;
    handleSearch: (searchFilters: FraudReportSearchFiltersState) => void;
};

type FraudReportSearchFormState = {
    selectedCompanyOption: SelectOption;
    selectedRpServiceOptions: Array<SelectOption>;
    selectedRelyingPartyOptions: Array<SelectOption>;
    selectedAssertionOptions: Array<SelectOption>;
    selectedRiskLevelOptions: Array<SelectOption>;
    selectedTimeRange: SelectOption;
    startDate: Date;
    endDate: Date;
};

export const FraudReportSearchForm = (props: FraudReportSearchFormProps) => {
    const maxDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => new Date(2015, 0, 1), []);
    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        requestInProgress,
        companies,
        relyingParties,
        rpServices,
        assertions,
        riskLevels,
        searchFilters,
        handleSearch
    } = props;

    const getFilterStateFromSearchFilters = (searchFilters: FraudReportSearchFiltersState): FraudReportSearchFormState => {
        let selectedCompany = companies.find(company => searchFilters.companyId === company.id);

        if (companies.length === 1) {
            selectedCompany = companies[0];
        }

        const selectedRpServices = rpServices.filter(rpService => searchFilters.rpServiceIds.includes(rpService.id));
        const selectedRelyingParties = relyingParties.filter(relyingParty => searchFilters.relyingPartyIds.includes(relyingParty.id));
        const selectedAssertions = assertions.filter(assertion => searchFilters.assertionIds.includes(assertion.id));
        const selectedRiskLevels = riskLevels.filter(riskLevel => searchFilters.riskLevelIds.includes(riskLevel.id));

        return {
            selectedCompanyOption: selectedCompany ? mapEntityWithNameAndIdToSelectOption(selectedCompany) : null,
            selectedRpServiceOptions: mapRpServicesToSelectOptions(selectedRpServices),
            selectedRelyingPartyOptions: mapEntitiesWithNameAndIdToSelectOptions(selectedRelyingParties),
            selectedAssertionOptions: mapAssertionsToSelectOptions(selectedAssertions),
            selectedRiskLevelOptions: mapRiskLevelsToSelectOptions(selectedRiskLevels),
            selectedTimeRange: {
                label: getTimeRangeLabel(searchFilters.timeRangeType),
                value: searchFilters.timeRangeType
            },
            startDate: searchFilters.startDate,
            endDate: searchFilters.endDate
        };
    };

    const [showFilters, setShowFilters] = useState(true);
    const [filterState, setFilterState] = useState<FraudReportSearchFormState>(getFilterStateFromSearchFilters(searchFilters));

    useEffect(() => {
        setFilterState(getFilterStateFromSearchFilters(searchFilters));
    }, [searchFilters]);

    const clearFilterState = () => {
        setFilterState(getFilterStateFromSearchFilters(initialFraudReportState.searchFilters));
    };

    const toggleShowFilters = () => {
        setShowFilters(!showFilters);
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const companyId = filterState.selectedCompanyOption?.value || null;
        const relyingPartyIds = mapSelectOptionsToValues(filterState.selectedRelyingPartyOptions);
        const rpServiceIds = mapSelectOptionsToValues(filterState.selectedRpServiceOptions);
        const assertionIds = mapSelectOptionsToValues(filterState.selectedAssertionOptions);
        const riskLevelIds = mapSelectOptionsToValues(filterState.selectedRiskLevelOptions);
        handleSearch({
            startDate: filterState.startDate,
            endDate: filterState.endDate,
            timeRangeType: filterState.selectedTimeRange.value as TimeRangeTypes,
            companyId,
            relyingPartyIds,
            rpServiceIds,
            assertionIds,
            riskLevelIds
        });
    };

    const handleUpdateCompanyOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedCompanyOption: newValue,
            selectedRpServiceOptions: [],
            selectedRelyingPartyOptions: []
        });
    };

    const handleUpdateRelyingPartyOptions = (newValue: Array<SelectOption>) => {
        setFilterState({
            ...filterState,
            selectedRelyingPartyOptions: newValue,
            selectedRpServiceOptions: []
        });
    };

    const handleUpdateRpServiceOptions = (newValue: Array<SelectOption>) => {
        setFilterState({
            ...filterState,
            selectedRpServiceOptions: newValue
        });
    };

    const handleUpdateAssertionOptions = (newValue: Array<SelectOption>) => {
        setFilterState({
            ...filterState,
            selectedAssertionOptions: newValue
        });
    };

    const handleUpdateRiskLevelOptions = (newValue: Array<SelectOption>) => {
        setFilterState({
            ...filterState,
            selectedRiskLevelOptions: newValue
        });
    };

    const handleTimeRangeChange = (newValue: SelectOption) => {
        if (newValue.value === TimeRangeTypes.CUSTOM) {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue
            });
        } else {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue,
                ...getTimeRange(newValue.value as TimeRangeTypes)
            });
        }
    };

    const handleStartDateChange = (newStartDate: Date) => {
        setFilterState({
            ...filterState,
            startDate: newStartDate
        });
    };

    const handleEndDateChange = (newEndDate: Date) => {
        setFilterState({
            ...filterState,
            endDate: newEndDate
        });
    };

    const relyingPartyOptions: Array<SelectOption> = useMemo(() => {
        const validRelyingParties = filterState.selectedCompanyOption
            ? relyingParties.filter(rp => rp.companyID === filterState.selectedCompanyOption.value)
            : relyingParties;

        return mapEntitiesWithNameAndIdToSelectOptions(validRelyingParties).sort((a, b) => sortStrings(a.label, b.label));
    }, [filterState.selectedCompanyOption, relyingParties]);

    const rpServiceOptions: Array<SelectOption> = useMemo(() => {
        const relyingPartyIds = filterState.selectedRelyingPartyOptions.length > 0
            ? mapSelectOptionsToValues(filterState.selectedRelyingPartyOptions)
            : mapSelectOptionsToValues(relyingPartyOptions);

        const validRpServices = rpServices.filter(service => relyingPartyIds.includes(service.relyingPartyID));
        return mapRpServicesToSelectOptions(validRpServices).sort((a, b) => sortStrings(a.label, b.label));
    }, [filterState.selectedRelyingPartyOptions, relyingPartyOptions, rpServices]);

    const assertionOptions: Array<SelectOption> = useMemo(() => {
        return mapAssertionsToSelectOptions(assertions);
    }, [assertions]);

    const riskLevelOptions: Array<SelectOption> = useMemo(() => {
        return mapRiskLevelsToSelectOptions(riskLevels);
    }, [riskLevels]);

    const timeRangeTypes = [
        TimeRangeTypes.TODAY,
        TimeRangeTypes.PAST_SEVEN_DAYS,
        TimeRangeTypes.CURRENT_MONTH,
        TimeRangeTypes.LAST_MONTH,
        TimeRangeTypes.CURRENT_QUARTER,
        TimeRangeTypes.LAST_QUARTER,
        TimeRangeTypes.CURRENT_YEAR,
        TimeRangeTypes.LAST_YEAR,
        TimeRangeTypes.CUSTOM
    ];

    return (
        <Form className={styles.fraudReportSearchForm} onSubmit={handleSubmit}>
            <SearchFormHeaderContainer
                idPrefix={"fraud-report"}
                showFilters={showFilters}
                showToggle={true}
                toggleShowFilters={toggleShowFilters}
            />
            {showFilters && (
                <>
                    <SearchFormFilterRow>
                        <OrganizationSelectFormGroup
                            idPrefix="fraud-report"
                            axnCompanies={companies}
                            isClearable={companies.length > 1}
                            isRequired={false}
                            layout={layout}
                            isSearchable={true}
                            labelWidth={labelWidth}
                            selectedOption={filterState.selectedCompanyOption}
                            onChange={handleUpdateCompanyOption}
                            disabled={companies.length === 1}
                        />
                        <MultiSelectFormGroup
                            automationId={`fraud-report-search-filters-relying-parties-select`}
                            controlId={`fraud-report-search-filters-relying-parties-select`}
                            label={resources.common.searchForm.filters.relyingParties.label}
                            ariaLabel={resources.common.searchForm.filters.relyingParties.label}
                            onChangeMulti={handleUpdateRelyingPartyOptions}
                            options={relyingPartyOptions}
                            selectedOptions={filterState.selectedRelyingPartyOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            noOptionsMessage={() => <span className={styles.redText}>{resources.common.searchForm.filters.relyingParties.noOptionsMessage}</span>}
                            keepMenuOpenOnSelect={true}
                        />
                        <MultiSelectFormGroup
                            automationId={`fraud-report-search-filters-services-select`}
                            controlId={`fraud-report-search-filters-services-select`}
                            label={resources.common.searchForm.filters.verificationServices.label}
                            ariaLabel={resources.common.searchForm.filters.verificationServices.label}
                            onChangeMulti={handleUpdateRpServiceOptions}
                            options={rpServiceOptions}
                            selectedOptions={filterState.selectedRpServiceOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            noOptionsMessage={() => <span className={styles.redText}>{resources.common.searchForm.filters.verificationServices.noOptionsMessage}</span>}
                            keepMenuOpenOnSelect={true}
                        />
                    </SearchFormFilterRow>
                    <SearchFormFilterRow>
                        <MultiSelectFormGroup
                            automationId={`fraud-report-search-filters-assertions-select`}
                            controlId={`fraud-report-search-filters-assertions-select`}
                            label={resources.pages.fraudReport.searchForm.filters.assertions.label}
                            ariaLabel={resources.pages.fraudReport.searchForm.filters.assertions.label}
                            onChangeMulti={handleUpdateAssertionOptions}
                            options={assertionOptions}
                            selectedOptions={filterState.selectedAssertionOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                        <MultiSelectFormGroup
                            automationId={`fraud-report-search-filters-risk-levels-select`}
                            controlId={`fraud-report-search-filters-risk-levels-select`}
                            label={resources.pages.fraudReport.searchForm.filters.riskLevels.label}
                            ariaLabel={resources.pages.fraudReport.searchForm.filters.riskLevels.label}
                            onChangeMulti={handleUpdateRiskLevelOptions}
                            options={riskLevelOptions}
                            selectedOptions={filterState.selectedRiskLevelOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={false}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                    </SearchFormFilterRow>
                    <TimeRangeSearchFormFilterRow
                        idPrefix={"fraud-report-search-filters"}
                        labelWidth={labelWidth}
                        layout={layout}
                        onChangeTimeRange={handleTimeRangeChange}
                        onChangeStartDate={handleStartDateChange}
                        onChangeEndDate={handleEndDateChange}
                        selectedTimeRange={filterState.selectedTimeRange}
                        timeRangeTypes={timeRangeTypes}
                        startDateLabel={resources.common.searchForm.filters.startDate.label}
                        startDateAriaLabel={resources.common.searchForm.filters.startDate.label}
                        endDateLabel={resources.common.searchForm.filters.endDate.label}
                        endDateAriaLabel={resources.common.searchForm.filters.endDate.label}
                        minDate={minDate}
                        maxDate={maxDate}
                        dateFormat={MONTH_YEAR_INPUT_DATE_FORMAT}
                        startDate={filterState.startDate}
                        endDate={filterState.endDate}
                        showMonthDropdown={false}
                        showYearDropdown={false}
                        showMonthYearPicker={true}
                    />
                    <div className={styles.buttonDiv}>
                        <Button id="fraud-report-search-clear-button" variant={"outline-secondary"} onClick={clearFilterState}>{resources.common.buttons.clear.label}</Button>
                        <ButtonWithSpinner
                            id="fraud-report-search-load-button" 
                            disabled={requestInProgress}
                            hideSpinner={false}
                            variant="primary"
                            type={ButtonTypes.SUBMIT}
                            label={resources.common.buttons.load.label}
                        />
                    </div>
                </>
            )}
        </Form>
    );
};